/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { Text, Box } from '@mantine/core';

// Styles
import classes from './RichTextEditor.module.css';
import './customStyle.css';

import {
	RichTextEditor as NativeRichTextEditor,
	Link,
	RichTextEditorControlProps,
} from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import Placeholder from '@tiptap/extension-placeholder';
import '@mantine/tiptap/styles.css';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type RichTextEditorProps = {
	label?: string;
	size: string;
	error?: boolean;
	onChange: (value: string) => void;
	maxWidth: string;
	placeholder?: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const RichTextEditor = (
	props: RichTextEditorControlProps & RichTextEditorProps
) => {
	const {
		onChange,
		value,
		label,
		size,
		error,
		maxWidth,
		placeholder,
	} = props;
	/* ******************************** HOOKS ******************************* */

	const editor = useEditor({
		extensions: [
			StarterKit,
			Placeholder.configure({
				placeholder: placeholder,
				emptyEditorClass: 'is-editor-empty',
				emptyNodeClass: 'is-editor-empty',
			}),
			Underline,
			Link,
			Superscript,
			SubScript,
			Highlight,
			TextAlign.configure({
				types: ['heading', 'paragraph'],
			}),
		],
		content: value?.toString(),
		onUpdate(props) {
			onChange(props.editor.getHTML());
		},
	});

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Text data-size={size} className={classes.label}>
				{label}{' '}
				<Text
					data-size={size}
					span
					inherit
					className={classes.asterisk}
				>
					{'*'}
				</Text>
			</Text>
			<NativeRichTextEditor
				classNames={classes}
				editor={editor}
				style={{
					maxWidth: maxWidth + 'px',
					border: error
						? '1px solid var(--mantine-color-flame-red)'
						: '',
				}}
				data-testid="M00kG199_Z5jdHi6B-input"
			>
				<NativeRichTextEditor.Toolbar
					sticky
					stickyOffset={60}
				>
					<NativeRichTextEditor.ControlsGroup>
						<NativeRichTextEditor.Bold />
						<NativeRichTextEditor.Italic />
						<NativeRichTextEditor.Underline />
						<NativeRichTextEditor.Strikethrough />
						<NativeRichTextEditor.ClearFormatting />
						<NativeRichTextEditor.Highlight />
						<NativeRichTextEditor.Code />
					</NativeRichTextEditor.ControlsGroup>

					<NativeRichTextEditor.ControlsGroup>
						<NativeRichTextEditor.Blockquote />
						<NativeRichTextEditor.Hr />
						<NativeRichTextEditor.BulletList />
						<NativeRichTextEditor.OrderedList />
						<NativeRichTextEditor.Subscript />
						<NativeRichTextEditor.Superscript />
					</NativeRichTextEditor.ControlsGroup>

					<NativeRichTextEditor.ControlsGroup>
						<NativeRichTextEditor.Link />
						<NativeRichTextEditor.Unlink />
					</NativeRichTextEditor.ControlsGroup>

					<NativeRichTextEditor.ControlsGroup>
						<NativeRichTextEditor.AlignLeft />
						<NativeRichTextEditor.AlignCenter />
						<NativeRichTextEditor.AlignJustify />
						<NativeRichTextEditor.AlignRight />
					</NativeRichTextEditor.ControlsGroup>

					<NativeRichTextEditor.ControlsGroup>
						<NativeRichTextEditor.Undo />
						<NativeRichTextEditor.Redo />
					</NativeRichTextEditor.ControlsGroup>
				</NativeRichTextEditor.Toolbar>

				<NativeRichTextEditor.Content />
			</NativeRichTextEditor>
			{error && (
				<Text data-size={size} className={classes.error}>
					{error}
				</Text>
			)}
		</Box>
	);
};
